import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { 
  ArrowLeft, 
  Search, 
  Users, 
  Briefcase, 
  Clock, 
  ChevronRight, 
  User,
  Building2, // Ajout icone
  Mail
} from 'lucide-react';
import type { Employee } from '../types';

export default function TeamManager() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [team, setTeam] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // --- CHARGEMENT ---
  // Les intérimaires ET les employés WAG (traités comme intérimaires) peuvent collaborer
  // avec n'importe qui, donc tous les employés les voient dans "Mon Équipe"
  const fetchTeam = useCallback(async () => {
    if (!user) return;
    setLoading(true);

    try {
      // Utiliser la RPC SECURITY DEFINER pour bypasser le RLS
      // (un électricien ne peut pas lire les autres employés via select direct)
      const { data, error } = await supabase.rpc('get_team_members');

      if (error) throw error;
      if (data) setTeam(data as unknown as Employee[]);

    } catch (error) {
      console.error("Erreur chargement équipe:", error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchTeam();
  }, [fetchTeam]);

  // Filtrage local
  const filteredTeam = team.filter(member => 
    member.full_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Helper pour les initiales
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();
  };

  return (
    <div style={{ fontFamily: 'Inter, sans-serif', paddingBottom: '80px' }}>
      
      {/* HEADER */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '30px' }}>
        <button 
          onClick={() => navigate('/')} 
          style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '10px', cursor: 'pointer', color:'#64748b' }}
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.5rem', color: '#1e293b' }}>Mon Équipe</h1>
          <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '0.9rem' }}>Gestion des intérimaires</p>
        </div>
      </div>

      {/* BARRE DE RECHERCHE */}
      <div style={{ position: 'relative', marginBottom: '24px' }}>
        <div style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}>
          <Search size={20} />
        </div>
        <input 
          type="text" 
          placeholder="Rechercher un membre..." 
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ 
            width: '100%', padding: '16px 16px 16px 48px', borderRadius: '12px', border: '1px solid #e2e8f0', fontSize: '1rem', outline: 'none',
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)', transition: 'border-color 0.2s'
          }}
        />
      </div>

      {/* LISTE / GRILLE */}
      {loading ? (
        <p style={{ textAlign: 'center', color: '#64748b', marginTop: '40px' }}>Chargement des effectifs...</p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
          
          {filteredTeam.length === 0 ? (
             <div style={{ gridColumn: '1 / -1', padding: '40px', background: 'white', borderRadius: '16px', textAlign: 'center', border: '1px dashed #cbd5e1', color: '#64748b' }}>
               <Users size={48} style={{ marginBottom: '15px', opacity: 0.3 }} />
               <p style={{ fontWeight: '500' }}>Aucun collaborateur trouvé.</p>
               {searchTerm && <p style={{ fontSize: '0.9rem' }}>Essayez une autre recherche.</p>}
             </div>
          ) : (
            filteredTeam.map(member => (
              <div 
                key={member.id} 
                style={{ 
                  background: 'white', 
                  border: '1px solid #e2e8f0', 
                  borderRadius: '16px', 
                  padding: '20px', 
                  boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px',
                  transition: 'transform 0.2s, box-shadow 0.2s'
                }}
              >
                {/* Info Membre */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                  <div style={{ width: '50px', height: '50px', borderRadius: '50%', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#475569', fontWeight: '700', fontSize: '1.1rem', border: '2px solid white', boxShadow: '0 0 0 2px #e2e8f0' }}>
                    {getInitials(member.full_name)}
                  </div>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#1e293b' }}>{member.full_name}</h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px' }}>
                      {member.is_interim ? (
                        <span style={{ background: '#fffbeb', color: '#b45309', fontSize: '0.75rem', fontWeight: '700', padding: '2px 8px', borderRadius: '6px', border: '1px solid #fcd34d', display:'flex', alignItems:'center', gap:'4px' }}>
                          INTÉRIMAIRE
                          {member.interim_agency && <Building2 size={10} />}
                        </span>
                      ) : (
                        <span style={{ background: '#f1f5f9', color: '#475569', fontSize: '0.75rem', fontWeight: '600', padding: '2px 8px', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <User size={12} /> {member.role}
                        </span>
                      )}
                      {member.email && (
                        <span style={{ fontSize: '0.75rem', color: '#64748b', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Mail size={11} /> {member.email}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div style={{ height: '1px', background: '#f1f5f9' }}></div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button 
                    onClick={() => navigate(`/team/profile/${member.id}`)}
                    style={{ flex: 1, padding: '10px', background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', color: '#64748b', cursor: 'pointer', fontWeight: '600', fontSize: '0.9rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                  >
                    <Briefcase size={16} /> Profil
                  </button>
                  
                  <button 
                    onClick={() => navigate(`/timesheet/${member.id}`)}
                    style={{ flex: 2, padding: '12px', background: '#2563eb', border: 'none', borderRadius: '8px', color: 'white', cursor: 'pointer', fontWeight: '600', fontSize: '0.9rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', boxShadow: '0 2px 4px rgba(37, 99, 235, 0.2)' }}
                  >
                    <Clock size={18} /> Saisir Heures <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}